'use client';

import { Loader2 } from 'lucide-react';
import { InventoryItemType } from '@/lib/services/inventory-types';
import { useAccountLawFirm } from '@/lib/hooks/useAccountPageData';
import type { LawFirm } from '@/lib/api/account-api';
import type { CustomInstitution } from '@/components/inventory/inventory-form-card/shared/InstitutionSearchField';
import { InventoryAttributeFormView } from './InventoryAttributeFormView';

const LEGAL_PROFESSIONAL_SLUG = 'legal-professional';

// The firm's own professional costs are the most common liability on a matter,
// so we pre-fill the Legal & Professional Costs Service Provider with the firm
// itself as an editable custom-institution sentinel (id: -1). On save the
// backend materialises it as a firm-custom professional-service institution
// once and reuses it thereafter; the field stays editable for external counsel
// (LF-5470).
function buildFirmServiceProvider(lawFirm: LawFirm): CustomInstitution {
  return {
    id: -1,
    name: lawFirm.name,
    email: lawFirm.email || lawFirm.valeEmail || '',
    type: 'custom',
    isCustom: true
  };
}

interface InventoryFormViewProps {
  selectedType: InventoryItemType;
  formData: {
    institution_id: string;
    estateReference: string;
  };
  dynamicFormData: Record<string, unknown>;
  submitting: boolean;
  onBack: () => void;
  onSubmit: (dynamicData: Record<string, unknown>) => void;
  onCancel: () => void;
  dateOfDeath?: string;
  matterId?: number;
  /**
   * Whether the reader has entered anything yet, reported up so
   * `AddInventoryDialog` can warn before a dismiss discards it (VALE-164).
   */
  onDirtyChange?: (isDirty: boolean) => void;
}

/**
 * **The add-inventory form step.** Since VALE-316 / `[INVDRAW-21]` there is one
 * treatment: {@link InventoryAttributeFormView} renders the record from the same
 * per-type spec builders the drawer uses, buffers each commit and sends one
 * `POST` on Save. The `InventoryFormCard` arm this used to select between is gone.
 *
 * **It is kept as a component rather than collapsed into its caller** because
 * the job below is real and belongs before the record mounts: resolving the
 * LF-5470 firm-as-service-provider seed, and holding the mount until the firm
 * profile that seed reads has resolved.
 */
export function InventoryFormView(props: InventoryFormViewProps) {
  const isLegalProfessional =
    props.selectedType?.slug === LEGAL_PROFESSIONAL_SLUG;
  const { lawFirm, isLoading: lawFirmLoading } = useAccountLawFirm();

  // The firm-as-payee default needs the firm profile, which is almost always
  // already cached by the time a lawyer is adding a liability. The record seeds
  // itself once, so briefly wait for the profile to resolve before mounting —
  // otherwise the default couldn't be pre-filled.
  if (isLegalProfessional && lawFirmLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="text-muted-foreground h-5 w-5 animate-spin" />
      </div>
    );
  }

  const defaultServiceProvider =
    isLegalProfessional && lawFirm
      ? buildFirmServiceProvider(lawFirm)
      : undefined;

  return (
    <InventoryAttributeFormView
      selectedType={props.selectedType}
      formData={props.formData}
      dynamicFormData={props.dynamicFormData}
      submitting={props.submitting}
      onBack={props.onBack}
      onSubmit={props.onSubmit}
      onCancel={props.onCancel}
      dateOfDeath={props.dateOfDeath}
      matterId={props.matterId}
      onDirtyChange={props.onDirtyChange}
      defaultServiceProvider={defaultServiceProvider}
    />
  );
}
